// Keranjang BUNDEL (pra-checkout) — helper MURNI, tanpa React.
//
// Bundel tak bisa menumpang `CartState` (satu produk per entri, baris dikunci
// `variationId` dan qty varian sama DIJUMLAH): komponennya lintas produk, dan
// baris bundel tak boleh melebur dengan baris biasa varian yang sama. Karena
// itu keranjang tersimpan (`galea-preorder-cart`) mendapat larik `bundles`
// SENDIRI di objek yang sama: `{ carts, bundles, owner }`. Pembaca lama
// (`normalizeSavedCarts`) mengabaikan kunci yang tak dikenalnya.
//
// Harga di sini HANYA tampilan — checkout menghitung ulang semuanya
// (`createCheckoutOrdersWithBundles`), persis seperti `CartLine.unitPrice`.

export const MAX_BUNDLE_QTY = 999;

export type CartBundleChoice = {
  componentId: string;
  productId: string;
  productName: string;
  productSlug: string;
  productImage: string;
  variationId: string;
  variationName: string;
  /** Label komponen ("Ayah", "Ibu") — tampilan. */
  label?: string | null;
  /**
   * Opsi varian pilihan (`variant_options.id`) — bahan "Ubah pilihan" untuk
   * produk PO mode malas, yang variannya tak termuat di halaman bundel. Bukan
   * bagian kunci entri; entri lama tanpa kolom ini kembali ke pilihan bawaan.
   */
  optionIds?: string[];
};

export type CartBundle = {
  /** bundleId + varian pilihan per komponen — dua pilihan berbeda = dua entri. */
  key: string;
  bundleId: string;
  slug: string;
  name: string;
  productType: "preorder" | "ready_stock";
  qty: number;
  choices: CartBundleChoice[];
  /** Harga per 1 bundel yang DILIHAT saat dimasukkan (tampilan, bisa basi). */
  displayTotal?: number;
  /** Harga terpisah per 1 bundel yang dilihat (tampilan). */
  displaySeparate?: number;
  /**
   * Σ harga tambahan per 1 bundel yang termasuk di harga yang dilihat (tampilan,
   * bughunt D15-11/26) — pembagi "Hemat %" atas dasar & catatan "Termasuk
   * tambahan" kartu potret. Entri lama tanpa kolom ini = tak diketahui.
   */
  displayExtras?: number;
};

export function cartBundleKey(bundleId: string, choices: readonly Pick<CartBundleChoice, "componentId" | "variationId">[]): string {
  const parts = [...choices]
    .sort((a, b) => a.componentId.localeCompare(b.componentId))
    .map((c) => `${c.componentId}=${c.variationId}`);
  return `${bundleId}::${parts.join("|")}`;
}

function clampQty(qty: number): number {
  if (!Number.isFinite(qty)) return 0;
  return Math.max(0, Math.min(MAX_BUNDLE_QTY, Math.floor(qty)));
}

/** Tambah bundel: pilihan sama = jumlah diakumulasi (maks 999); fungsi murni. */
export function addBundleToCart(bundles: readonly CartBundle[], entry: Omit<CartBundle, "key">): CartBundle[] {
  const qty = clampQty(entry.qty);
  if (qty <= 0 || entry.choices.length < 2) return [...bundles];
  const key = cartBundleKey(entry.bundleId, entry.choices);
  const idx = bundles.findIndex((b) => b.key === key);
  if (idx >= 0) {
    return bundles.map((b, i) =>
      i === idx ? { ...b, ...entry, key, qty: clampQty(b.qty + qty) } : b
    );
  }
  return [...bundles, { ...entry, key, qty }];
}

/**
 * "Ubah pilihan" dari keranjang (`/bundling/<slug>?ganti=<kunci>`): entri
 * `oldKey` DIGANTI `entry` dalam SATU pembaruan — tak pernah dua entri
 * (lama + baru) dan tak pernah jumlah yang diam-diam bertambah.
 *   • kunci baru = `oldKey` → jumlahnya DISETEL (bukan ditambah), posisi tetap;
 *   • kunci baru = entri LAIN yang sudah ada → digabung ke entri itu (jumlah
 *     dijumlah, maks 999) dan entri lama dibuang;
 *   • selain itu → entri baru menempati posisi entri lama.
 * GANTI-SAJA: `oldKey` yang tak ada di keranjang = keranjang TAK BERUBAH —
 * bukan tambah biasa. Ketukan kedua "Simpan perubahan" (atau closure basi)
 * datang sesudah entri lama sudah diganti; menambahkannya lagi menggandakan
 * jumlah bundel. Kunci `?ganti` yang memang tak ada sejak awal ditangani
 * halaman bundel sebagai tambah biasa (`addBundleToCart`), bukan di sini.
 * Entri tak sah (jumlah ≤ 0 / < 2 komponen) = keranjang tak berubah. Murni.
 */
export function replaceBundleInCart(
  bundles: readonly CartBundle[],
  oldKey: string,
  entry: Omit<CartBundle, "key">
): CartBundle[] {
  const qty = clampQty(entry.qty);
  if (qty <= 0 || entry.choices.length < 2) return [...bundles];
  const oldIdx = bundles.findIndex((b) => b.key === oldKey);
  if (oldIdx < 0) return [...bundles];
  const key = cartBundleKey(entry.bundleId, entry.choices);
  if (key === oldKey) {
    return bundles.map((b, i) => (i === oldIdx ? { ...b, ...entry, key, qty } : b));
  }
  const otherIdx = bundles.findIndex((b) => b.key === key);
  if (otherIdx >= 0) {
    return bundles
      .map((b, i) => (i === otherIdx ? { ...b, ...entry, key, qty: clampQty(b.qty + qty) } : b))
      .filter((_, i) => i !== oldIdx);
  }
  return bundles.map((b, i) => (i === oldIdx ? { ...entry, key, qty } : b));
}

/** Ubah jumlah bundel; ≤ 0 = hapus. */
export function updateBundleQtyInCart(bundles: readonly CartBundle[], key: string, qty: number): CartBundle[] {
  const q = clampQty(qty);
  if (q <= 0) return bundles.filter((b) => b.key !== key);
  return bundles.map((b) => (b.key === key ? { ...b, qty: q } : b));
}

export function removeBundleFromCart(bundles: readonly CartBundle[], key: string): CartBundle[] {
  return bundles.filter((b) => b.key !== key);
}

/** Σ pcs bundel (k × komponen) — untuk badge jumlah barang keranjang. */
export function cartBundlesTotalPcs(bundles: readonly CartBundle[]): number {
  return bundles.reduce((s, b) => s + b.qty * b.choices.length, 0);
}

/** Subtotal tampilan; null bila ada bundel yang harganya tak diketahui. */
export function cartBundlesSubtotal(bundles: readonly CartBundle[]): number | null {
  let total = 0;
  for (const b of bundles) {
    if (b.displayTotal === undefined || !Number.isFinite(b.displayTotal)) return null;
    total += b.displayTotal * b.qty;
  }
  return total;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");

function normalizeChoice(raw: unknown): CartBundleChoice | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const componentId = str(r.componentId);
  const productId = str(r.productId);
  const variationId = str(r.variationId);
  if (!componentId || !productId || !variationId) return null;
  const optionIds = Array.isArray(r.optionIds)
    ? r.optionIds.filter((o): o is string => typeof o === "string" && o.length > 0)
    : [];
  return {
    componentId,
    productId,
    productName: str(r.productName),
    productSlug: str(r.productSlug),
    productImage: str(r.productImage),
    variationId,
    variationName: str(r.variationName),
    label: typeof r.label === "string" && r.label.trim() ? r.label : null,
    ...(optionIds.length ? { optionIds } : {})
  };
}

/**
 * Larik `bundles` dari JSON tersimpan. Bentuk lama (tanpa kunci `bundles`,
 * array, satu objek keranjang) = tanpa bundel. Entri rusak dibuang satu-satu,
 * bukan seluruh keranjang.
 */
export function normalizeSavedBundles(raw: unknown): CartBundle[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const list = (raw as Record<string, unknown>).bundles;
  if (!Array.isArray(list)) return [];
  const out: CartBundle[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const bundleId = str(r.bundleId);
    const choices = Array.isArray(r.choices)
      ? r.choices.map(normalizeChoice).filter((c): c is CartBundleChoice => c !== null)
      : [];
    const qty = clampQty(Number(r.qty));
    if (!bundleId || choices.length < 2 || qty <= 0) continue;
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
    out.push({
      key: cartBundleKey(bundleId, choices),
      bundleId,
      slug: str(r.slug),
      name: str(r.name) || "Bundling",
      productType: r.productType === "preorder" ? "preorder" : "ready_stock",
      qty,
      choices,
      displayTotal: num(r.displayTotal),
      displaySeparate: num(r.displaySeparate),
      displayExtras: num(r.displayExtras)
    });
  }
  return out;
}
